/**
 * AIPhotoCheck — small button + modal to run Claude 4.5 vision on an already-
 * uploaded photo. Shows findings, lets certifier copy them into the item's
 * notes field (or dismiss).
 *
 * Court safety: NEVER auto-apply the AI's compliance verdict — the certifier
 * must review + accept.
 */
import { useState } from "react";
import { Sparkles, Loader2, X, CheckCircle2, AlertTriangle, HelpCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isLazyPhoto, resolveLazyPhoto } from "@/lib/lazyPhotos";
import { useOverlayBackClose } from "@/hooks/backGuard";

const CONFIDENCE_COLORS = {
  high: "bg-emerald-100 text-emerald-900 border-emerald-400",
  medium: "bg-amber-100 text-amber-900 border-amber-400",
  low: "bg-slate-100 text-slate-700 border-slate-300",
};

const AIPhotoCheck = ({ inspectionId, photoBase64, questionId, questionLabel, onApplyToNotes }) => {
  const [open, setOpen] = useState(false);
  // Phone back button closes this dialog instead of leaving the checklist.
  useOverlayBackClose(open, () => setOpen(false));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedSummary, setCopiedSummary] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    setOpen(true);
    try {
      let photo = photoBase64;
      if (isLazyPhoto(photo)) photo = await resolveLazyPhoto(inspectionId, photo);
      const r = await api.post(`/inspections/${inspectionId}/photo-check`, {
        image_base64: photo,
        question_id: questionId,
        question_label: questionLabel,
      });
      setResult(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI photo check failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const copySummary = () => {
    if (!result?.overall_summary) return;
    const lines = [
      result.overall_summary,
      "",
      ...(result.findings || []).map((f) =>
        `• ${f.label}${f.detected_value ? " — " + f.detected_value : ""} (${f.confidence} confidence)${f.standard_reference ? " [" + f.standard_reference + "]" : ""}`
      ),
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    setCopiedSummary(true);
    toast.success("Findings copied to clipboard");
    setTimeout(() => setCopiedSummary(false), 2000);
  };

  const applyToNotes = () => {
    if (!result || !onApplyToNotes) return;
    const verdictLine = result.verdict === "insufficient_evidence"
      ? "AI verdict: insufficient evidence — manual assessment required."
      : `AI verdict: ${result.verdict === "compliant" ? "COMPLIANT" : "NON-COMPLIANT"} (confidence ${result.confidence}%).`;
    const lines = [
      "AI Photo Analysis (Claude 4.5) — please verify:",
      verdictLine,
      result.overall_summary,
      ...(result.findings || []).map((f) =>
        `• ${f.label}: ${f.detected_value} [${f.confidence}]`
      ),
    ];
    onApplyToNotes(lines.join("\n"));
    toast.success("Added to item notes — please review");
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={run}
        data-testid={`ai-photo-check-${questionId}`}
        className="h-9 px-3 inline-flex items-center gap-1.5 border-2 border-purple-600 bg-purple-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-purple-700 disabled:opacity-60"
        title="Analyse this photo with Claude 4.5 AI"
      >
        <Sparkles size={13} strokeWidth={2.5}/>
        AI check
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)} data-testid="ai-photo-modal">
          <div className="bg-white border-2 border-slate-200 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <header className="px-5 py-4 border-b-2 border-slate-200 flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700 flex items-center gap-1.5">
                  <Sparkles size={11} strokeWidth={3}/> Claude 4.5 vision · AI photo compliance
                </p>
                <h2 className="text-lg font-black text-slate-900">
                  {questionLabel || "Photo analysis"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className="font-bold text-purple-700">AI locked to this checklist item only.</span>{" "}
                  Findings are advisory. You are the certifier — verify every measurement before signing off.
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-photo-modal-close" className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100 shrink-0"><X size={16}/></button>
            </header>
            <div className="p-5">
              {loading && (
                <div className="py-12 text-center text-slate-600" data-testid="ai-photo-loading">
                  <Loader2 size={32} className="animate-spin mx-auto text-purple-600"/>
                  <p className="mt-3 font-bold">Analysing the photo…</p>
                  <p className="text-xs mt-1">Claude is measuring fence heights, gaps and non-climbable zones. This takes 5–10 seconds.</p>
                </div>
              )}
              {result && !loading && (
                <div className="space-y-4" data-testid="ai-photo-result">
                  {result.verdict === "insufficient_evidence" ? (
                    <div className="p-4 border-2 border-amber-400 bg-amber-50" data-testid="ai-verdict-insufficient">
                      <p className="text-xs font-black uppercase tracking-[0.15em] text-amber-900 flex items-center gap-1.5">
                        <HelpCircle size={13} strokeWidth={3}/> More evidence needed — no compliance decision made
                      </p>
                      {typeof result.confidence === "number" && (
                        <p className="text-xs text-amber-900 mt-1"><strong>Confidence: {result.confidence}%</strong>{result.confidence_reason ? ` — ${result.confidence_reason}` : ""}</p>
                      )}
                      {(result.evidence_requests || []).length > 0 && (
                        <ul className="mt-2 text-sm text-amber-950 list-disc pl-5 space-y-0.5">
                          {result.evidence_requests.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      )}
                    </div>
                  ) : result.verdict === "compliant" ? (
                    <div className="p-4 border-2 border-emerald-400 bg-emerald-50" data-testid="ai-verdict-compliant">
                      <p className="text-sm font-black text-emerald-900 flex items-center gap-1.5">
                        <CheckCircle2 size={15} strokeWidth={2.5}/> COMPLIANT — Confidence {result.confidence}%
                      </p>
                      {result.confidence_reason && <p className="text-xs text-emerald-900 mt-1">{result.confidence_reason}</p>}
                    </div>
                  ) : result.verdict === "non_compliant" ? (
                    <div className="p-4 border-2 border-red-400 bg-red-50" data-testid="ai-verdict-noncompliant">
                      <p className="text-sm font-black text-red-900 flex items-center gap-1.5">
                        <AlertTriangle size={15} strokeWidth={2.5}/> NON-COMPLIANT — Confidence {result.confidence}%
                      </p>
                      {result.confidence_reason && <p className="text-xs text-red-900 mt-1">{result.confidence_reason}</p>}
                    </div>
                  ) : null}
                  {result.overall_summary && (
                    <div className="p-4 border-2 border-slate-200 bg-slate-50">
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Summary</p>
                      <p className="text-sm text-slate-800 leading-relaxed">{result.overall_summary}</p>
                    </div>
                  )}
                  {(result.kb_used || []).length > 0 && (
                    <p className="text-[11px] text-slate-500" data-testid="ai-kb-used">
                      <strong>Knowledge base referenced:</strong> {result.kb_used.join(" · ")}
                    </p>
                  )}
                  {(result.findings || []).length > 0 ? (
                    <ul className="space-y-2">
                      {result.findings.map((f, idx) => (
                        <li key={idx} className={`p-3 border-2 ${CONFIDENCE_COLORS[f.confidence] || CONFIDENCE_COLORS.medium}`} data-testid={`ai-finding-${idx}`}>
                          <div className="flex items-start gap-2">
                            {f.is_compliant === true ? <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0" strokeWidth={2.5}/>
                              : f.is_compliant === false ? <AlertTriangle size={16} className="text-red-700 mt-0.5 shrink-0" strokeWidth={2.5}/>
                              : <HelpCircle size={16} className="text-slate-500 mt-0.5 shrink-0" strokeWidth={2.5}/>}
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm">{f.label}</div>
                              {f.detected_value && <div className="text-xs mt-0.5"><strong>Detected:</strong> {f.detected_value}</div>}
                              {f.notes && <div className="text-xs mt-1 opacity-90">{f.notes}</div>}
                              {f.standard_reference && <div className="text-[10px] mt-1 opacity-70 uppercase tracking-wider">{f.standard_reference}</div>}
                              <div className="text-[10px] mt-1 uppercase tracking-wider opacity-70">{f.confidence} confidence</div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-600" data-testid="ai-no-findings">No specific findings — try a clearer photo showing the fence/gate from directly in front.</p>
                  )}
                  {(result.recommended_actions || []).length > 0 && (
                    <div className="p-3 border-2 border-blue-200 bg-blue-50">
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-blue-900 mb-1">Recommended actions</p>
                      <ul className="text-xs text-blue-900 list-disc pl-5 space-y-0.5">
                        {result.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            {result && !loading && (
              <footer className="px-5 py-4 border-t-2 border-slate-200 bg-slate-50 flex flex-wrap justify-end gap-2">
                <button data-testid="ai-photo-copy" onClick={copySummary} className="h-10 px-3 border-2 border-slate-300 bg-white text-slate-900 font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5 hover:border-slate-500">
                  {copiedSummary ? <><Check size={12} strokeWidth={3}/> Copied</> : <><Copy size={12} strokeWidth={3}/> Copy</>}
                </button>
                {onApplyToNotes && (
                  <button data-testid="ai-photo-apply" onClick={applyToNotes} className="h-10 px-4 bg-purple-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-purple-700 inline-flex items-center gap-1.5">
                    <Sparkles size={12} strokeWidth={3}/> Add to notes
                  </button>
                )}
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIPhotoCheck;
